import React, { useState, useEffect } from 'react';
import { Card, Col, Row, Typography, Tag, Button, Progress, Space, message, Badge, Divider } from 'antd';
import { ReloadOutlined, DatabaseOutlined, HddOutlined, ApiOutlined, CloudOutlined, WarningOutlined, CheckCircleOutlined } from '@ant-design/icons';
import api from '../api';

const { Title, Text } = Typography;

function SystemMonitorPage() {
  const [metrics, setMetrics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const data = await api.get('/admin/system/metrics');
      setMetrics(data.metrics);
      setAlerts(data.alerts || []);
    } catch (e) {
      message.error(e.error || '获取监控数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    const timer = setInterval(fetchMetrics, 30000);
    return () => clearInterval(timer);
  }, []);

  const handleBackup = async () => {
    try {
      const res = await api.post('/admin/system/backup');
      message.success(res.message || '备份创建成功');
    } catch (e) {
      message.error('备份失败');
    }
  };

  if (!metrics) return <div style={{ padding: 24, textAlign: 'center' }}>加载中...</div>;

  const memUsage = metrics.memory?.usage || 0;
  const dbConnections = metrics.database?.connections || 0;
  const redisAvailable = metrics.redis?.available || false;
  const cpuCount = metrics.cpu?.count || 0;
  const loadAvg = metrics.cpu?.loadAvg?.[0]?.toFixed(2) || '0.00';

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>系统监控</Title>
        <Button icon={<ReloadOutlined />} onClick={fetchMetrics} loading={loading}>刷新</Button>
      </div>

      {/* 告警 */}
      {alerts.length > 0 && (
        <Card size="small" style={{ marginBottom: 16, background: '#fff7e6', border: '1px solid #ffd591' }}>
          {alerts.map((a, i) => (
            <div key={i} style={{ marginBottom: 4 }}>
              <Tag color={a.level === 'critical' ? 'red' : 'orange'}>{a.level === 'critical' ? '严重' : '警告'}</Tag>
              <Text>{a.message}</Text>
            </div>
          ))}
        </Card>
      )}

      {/* 指标卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card size="small" loading={loading}>
            <div style={{ textAlign: 'center' }}>
              <HddOutlined style={{ fontSize: 24, color: '#07C160' }} />
              <div style={{ fontSize: 24, fontWeight: 'bold', color: memUsage > 80 ? '#ff4d4f' : '#07C160' }}>{memUsage}%</div>
              <Text type="secondary">内存使用率</Text>
              <Progress percent={memUsage} showInfo={false} strokeColor={memUsage > 80 ? '#ff4d4f' : '#07C160'} />
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" loading={loading}>
            <div style={{ textAlign: 'center' }}>
              <ApiOutlined style={{ fontSize: 24, color: '#10AEFF' }} />
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#10AEFF' }}>{cpuCount}</div>
              <Text type="secondary">CPU 核心数</Text>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">负载: {loadAvg}</Text>
              </div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" loading={loading}>
            <div style={{ textAlign: 'center' }}>
              <DatabaseOutlined style={{ fontSize: 24, color: '#6467F0' }} />
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#6467F0' }}>{dbConnections}</div>
              <Text type="secondary">数据库连接数</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small" loading={loading}>
            <div style={{ textAlign: 'center' }}>
              <CloudOutlined style={{ fontSize: 24, color: redisAvailable ? '#07C160' : '#ff4d4f' }} />
              <div style={{ fontSize: 24, fontWeight: 'bold', color: redisAvailable ? '#07C160' : '#ff4d4f' }}>
                {redisAvailable ? '在线' : '离线'}
              </div>
              <Text type="secondary">Redis 状态</Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 详细指标 */}
      <Row gutter={16}>
        <Col span={12}>
          <Card title="📊 内存使用详情" size="small" loading={loading}>
            <div style={{ padding: '20px 0' }}>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 36, fontWeight: 'bold', color: memUsage > 80 ? '#ff4d4f' : '#07C160' }}>{memUsage}%</div>
                <Text type="secondary">已用内存</Text>
              </div>
              <Progress
                type="circle"
                percent={memUsage}
                strokeColor={memUsage > 80 ? '#ff4d4f' : '#07C160'}
                format={(p) => `${p}%`}
                size={120}
                style={{ display: 'block', margin: '0 auto' }}
              />
            </div>
            <Divider style={{ margin: '12px 0' }} />
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">总内存</Text>
                <Text>{((metrics.memory?.total || 0) / 1024 / 1024 / 1024).toFixed(2)} GB</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">已用内存</Text>
                <Text>{((metrics.memory?.used || 0) / 1024 / 1024 / 1024).toFixed(2)} GB</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">空闲内存</Text>
                <Text>{((metrics.memory?.free || 0) / 1024 / 1024 / 1024).toFixed(2)} GB</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="💾 系统信息" size="small" loading={loading}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button type="primary" icon={<DatabaseOutlined />} onClick={handleBackup} style={{ width: '100%', background: '#07C160' }}>立即备份</Button>
              <Divider style={{ margin: '12px 0' }} />
              <Text strong>Node.js 信息</Text>
              <div><Text type="secondary">版本：{metrics.node?.version || '-'}</Text></div>
              <div><Text type="secondary">PID：{metrics.node?.pid || '-'}</Text></div>
              <div><Text type="secondary">运行时间：{Math.floor((metrics.uptime || 0) / 3600)} 小时 {Math.floor(((metrics.uptime || 0) % 3600) / 60)} 分钟</Text></div>
              <Divider style={{ margin: '12px 0' }} />
              <Text strong>服务状态</Text>
              <div>
                <Badge status={redisAvailable ? 'success' : 'error'} />
                <Text style={{ marginLeft: 8 }}>Redis: {redisAvailable ? '正常' : '异常'}</Text>
              </div>
              <div>
                <Badge status="success" />
                <Text style={{ marginLeft: 8 }}>数据库: 正常 ({dbConnections} 连接)</Text>
              </div>
              <div>
                <Badge status={memUsage > 80 ? 'error' : 'success'} />
                <Text style={{ marginLeft: 8 }}>内存: {memUsage > 80 ? '告警' : '正常'}</Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>
    </>
  );
}

export default SystemMonitorPage;
